/**
 * Error Handling Utilities
 * 
 * Centralized error handling to ensure consistent error messages
 * and user-friendly error reporting across the application.
 */

/**
 * Extract a user-friendly error message from an unknown error
 * 
 * Handles various error types (Error objects, strings, Supabase errors, etc.)
 * and returns a consistent string message.
 * 
 * @param error - The error to handle (can be any type)
 * @param fallback - Default message if error cannot be parsed
 * @returns A user-friendly error message
 * 
 * @example
 * ```typescript
 * try {
 *     await someOperation();
 * } catch (err) {
 *     const message = handleError(err, 'Operation failed');
 *     setError(message);
 * }
 * ```
 */
export function handleError(error: unknown, fallback: string): string {
    // Handle Error instances
    if (error instanceof Error) {
        return error.message;
    }

    // Handle string errors
    if (typeof error === 'string') {
        return error;
    }

    // Handle Supabase/PostgreSQL errors with error property
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message: unknown }).message);
    }

    // Log unexpected error types for debugging
    console.error('Unexpected error type:', error);

    return fallback;
}

/**
 * Log error with context for debugging
 * 
 * Useful for development and can be extended to send to error monitoring
 * services like Sentry, LogRocket, etc.
 * 
 * @param error - The error to log
 * @param context - Description of where/when the error occurred
 * @param metadata - Optional additional data for debugging
 * 
 * @example
 * ```typescript
 * try {
 *     await expensiveOperation();
 * } catch (err) {
 *     logError(err, 'Failed to load expenses', { userId, filters });
 *     throw err;
 * }
 * ```
 */
export function logError(
    error: unknown,
    context: string,
    metadata?: Record<string, unknown>
): void {
    const message = handleError(error, 'Unknown error');

    console.error(`[${context}]`, message);

    if (metadata) {
        console.error('Metadata:', metadata);
    }

    if (error instanceof Error && error.stack) {
        console.error('Stack:', error.stack);
    }

    // TODO: Send to error monitoring service (Sentry, LogRocket, etc.)
    // if (import.meta.env.PROD) {
    //     Sentry.captureException(error, { tags: { context }, extra: metadata });
    // }
}

/**
 * Validation error class for business logic validation failures
 * 
 * Use this for expected validation errors that should be shown to users.
 * Distinguished from unexpected system errors.
 */
export class ValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ValidationError';
    }
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): error is ValidationError {
    return error instanceof ValidationError;
}
